import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
    ChevronDown,
    Eye,
    FileCode,
    FilePlus2,
    Loader2,
    Lock,
    PenLine,
    Upload,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Skeleton } from '@/components/ui/skeleton';
import { captureError } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { DOC_TYPES, PARTY_LABELS, parseAgreementError } from '@/lib/agreements/constants';
import {
    createTemplateDraft,
    fetchAgreementTemplates,
    publishTemplate,
} from '@/lib/agreements/templates';
import {
    groupTemplates,
    type AgreementTemplateSummary,
    type TemplateFamily,
} from '@/lib/agreements/templateCatalog';
import PublishTemplateDialog from '@/components/agreements/PublishTemplateDialog';
import TemplateEditorSheet from '@/components/agreements/TemplateEditorSheet';

interface PublishTarget {
    id: number;
    label: string;
    version: number;
    replacingVersion: number | null;
}

/**
 * Agreement templates — the wording every generated document is set from.
 *
 * THE PAGE EXISTS SO A WORDING CHANGE DOES NOT NEED AN ENGINEER. Before it, a new version of
 * an agreement was a migration; here it is copy the current version into a draft, edit the
 * markdown, read the preview, publish. What it deliberately does NOT offer is an edit of a
 * published version: `agreement_documents.template_id` pins what each document was generated
 * from, and a template that can be rewritten under a signed document makes every archived
 * signature unverifiable. The immutability trigger enforces that server-side; this page just
 * never asks for something it would refuse.
 *
 * ACCESS IS TWO CHECKS OFF ONE DEFINITION ROW, and they read different columns of it. The
 * `PermissionGuard` in `App.tsx` keys on `agreements.templates` — the definition's KEY, and the
 * same key the create-draft, update-draft and publish RPCs each enforce for themselves, so the
 * page can never offer an action the database is going to refuse. The nav entry and
 * `DashboardLayout`'s redirect key instead on the definition's ROUTES, which carry `agreements`
 * because `canAccessRoute` matches the first path segment after /dashboard. An admin holding the
 * key therefore both sees the link and survives the redirect; everybody else keeps landing back
 * on their default route. Superadmins (zero permission rows) pass both checks unconditionally.
 */
export default function AgreementTemplates() {
    const queryClient = useQueryClient();

    const [editingId, setEditingId] = useState<number | null>(null);
    const [publishTarget, setPublishTarget] = useState<PublishTarget | null>(null);

    const { data: templates, isLoading } = useQuery({
        queryKey: ['agreement-templates'],
        queryFn: fetchAgreementTemplates,
    });

    const families = useMemo(() => groupTemplates(templates ?? []), [templates]);

    const fail = (error: unknown) => {
        captureError(error);
        toast.error(parseAgreementError(error).message);
    };

    const createDraft = useMutation({
        mutationFn: (templateKey: string) => createTemplateDraft(templateKey),
        onSuccess: (result) => {
            toast.success(`Draft v${result.version} created`);
            queryClient.invalidateQueries({ queryKey: ['agreement-templates'] });
            // Straight into the editor: a draft with nobody editing it is a half-finished
            // change sitting where the next admin will wonder what it was for.
            setEditingId(result.template_id);
        },
        onError: fail,
    });

    const publish = useMutation({
        mutationFn: (templateId: number) => publishTemplate(templateId),
        onSuccess: (result, templateId) => {
            toast.success(`Published v${result.version}`);
            queryClient.invalidateQueries({ queryKey: ['agreement-templates'] });
            queryClient.invalidateQueries({ queryKey: ['agreement-template-body', templateId] });
            setPublishTarget(null);
            // The row is sealed now, so an editor still open on it would be lying about
            // what it can do.
            if (editingId === templateId) setEditingId(null);
        },
        onError: fail,
    });

    return (
        <div className="animate-fade-in space-y-6 px-4 pt-6 sm:px-6 lg:px-8">
            <div className="mb-8">
                <div className="mb-2 flex items-center gap-3">
                    <FileCode className="h-8 w-8" />
                    <h1 className="text-4xl font-semibold tracking-tight">Agreement Templates</h1>
                </div>
                <p className="text-muted-foreground">
                    The markdown every generated agreement is set from, versioned. Publishing a version
                    changes what the next document says; documents already generated keep theirs.
                </p>
            </div>

            {isLoading ? (
                <div className="space-y-4">
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-40 w-full" />
                    <Skeleton className="h-40 w-full" />
                </div>
            ) : families.length === 0 ? (
                <Card>
                    <CardContent className="py-10 text-center text-sm text-muted-foreground">
                        No templates are seeded on this environment yet.
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-4">
                    {families.map((family) => (
                        <FamilyCard
                            key={family.key}
                            family={family}
                            creating={createDraft.isPending && createDraft.variables === family.key}
                            onCreateDraft={() => createDraft.mutate(family.key)}
                            onEdit={(templateId) => setEditingId(templateId)}
                            onPublish={(draft) =>
                                setPublishTarget({
                                    id: draft.id,
                                    label: family.label,
                                    version: draft.version,
                                    replacingVersion: family.published?.version ?? null,
                                })
                            }
                        />
                    ))}
                </div>
            )}

            <TemplateEditorSheet
                templateId={editingId}
                onOpenChange={(open) => !open && setEditingId(null)}
                onPublish={(template) => {
                    const family = families.find((candidate) => candidate.key === template.template_key);
                    setPublishTarget({
                        id: template.id,
                        label: family?.label ?? template.title,
                        version: template.version,
                        replacingVersion: family?.published?.version ?? null,
                    });
                }}
            />

            {publishTarget && (
                <PublishTemplateDialog
                    open
                    onOpenChange={(open) => !open && !publish.isPending && setPublishTarget(null)}
                    label={publishTarget.label}
                    version={publishTarget.version}
                    replacingVersion={publishTarget.replacingVersion}
                    isPending={publish.isPending}
                    onConfirm={() => publish.mutate(publishTarget.id)}
                />
            )}
        </div>
    );
}

// ─── One template family ─────────────────────────────────────────────────────

function FamilyCard({
    family,
    creating,
    onCreateDraft,
    onEdit,
    onPublish,
}: {
    family: TemplateFamily;
    creating: boolean;
    onCreateDraft: () => void;
    onEdit: (templateId: number) => void;
    onPublish: (draft: AgreementTemplateSummary) => void;
}) {
    const [historyOpen, setHistoryOpen] = useState(false);

    const meta = family.docType ? DOC_TYPES[family.docType] : undefined;
    const Icon = meta?.icon ?? FileCode;
    const parties = meta?.parties.map((party) => PARTY_LABELS[party] ?? party).join(' → ');

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                        <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                            <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                            <CardTitle className="flex flex-wrap items-center gap-2">
                                {family.label}
                                {family.published ? (
                                    <Badge
                                        variant="outline"
                                        className="gap-1 border-emerald-500/40 font-normal text-emerald-700 dark:text-emerald-400"
                                    >
                                        <Lock className="h-3 w-3" />
                                        Published v{family.published.version}
                                    </Badge>
                                ) : (
                                    <Badge variant="outline" className="font-normal text-destructive">
                                        No published version
                                    </Badge>
                                )}
                                {family.draft && (
                                    <Badge
                                        variant="outline"
                                        className="gap-1 border-amber-500/40 font-normal text-amber-700 dark:text-amber-400"
                                    >
                                        <PenLine className="h-3 w-3" />
                                        Draft v{family.draft.version}
                                    </Badge>
                                )}
                            </CardTitle>
                            <CardDescription className="mt-1 flex flex-wrap items-center gap-x-2">
                                <span className="font-mono">{family.key}</span>
                                {parties && <span>·</span>}
                                {parties && <span>{parties}</span>}
                                {family.published?.effective_at && (
                                    <>
                                        <span>·</span>
                                        <span>
                                            current since{' '}
                                            {format(new Date(family.published.effective_at), 'MMM d, yyyy')}
                                        </span>
                                    </>
                                )}
                            </CardDescription>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        {family.draft ? (
                            <>
                                <Button size="sm" onClick={() => onEdit(family.draft!.id)}>
                                    <PenLine className="mr-2 h-4 w-4" />
                                    Edit draft
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => onPublish(family.draft!)}>
                                    <Upload className="mr-2 h-4 w-4" />
                                    Publish v{family.draft.version}
                                </Button>
                            </>
                        ) : (
                            <Button size="sm" onClick={onCreateDraft} disabled={creating}>
                                {creating ? (
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                ) : (
                                    <FilePlus2 className="mr-2 h-4 w-4" />
                                )}
                                Create draft
                            </Button>
                        )}
                        {family.published && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => onEdit(family.published!.id)}
                            >
                                <Eye className="mr-2 h-4 w-4" />
                                View published
                            </Button>
                        )}
                    </div>
                </div>
            </CardHeader>

            <CardContent>
                <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
                    <CollapsibleTrigger asChild>
                        <Button variant="ghost" size="sm" className="px-2 text-muted-foreground">
                            <ChevronDown
                                className={cn('mr-2 h-4 w-4 transition-transform', historyOpen && 'rotate-180')}
                            />
                            Version history ({family.versions.length})
                        </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-2 space-y-1">
                        {family.versions.map((version) => (
                            <VersionRow
                                key={version.id}
                                version={version}
                                onOpen={() => onEdit(version.id)}
                            />
                        ))}
                    </CollapsibleContent>
                </Collapsible>
            </CardContent>
        </Card>
    );
}

function VersionRow({
    version,
    onOpen,
}: {
    version: AgreementTemplateSummary;
    onOpen: () => void;
}) {
    const when =
        version.status === 'retired' && version.retired_at
            ? `retired ${format(new Date(version.retired_at), 'MMM d, yyyy')}`
            : version.status === 'published' && version.effective_at
                ? `published ${format(new Date(version.effective_at), 'MMM d, yyyy')}`
                : `created ${format(new Date(version.created_at), 'MMM d, yyyy')}`;

    return (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <span className="font-medium">v{version.version}</span>
                <Badge variant="outline" className="font-normal capitalize">
                    {version.status}
                </Badge>
                <span className="text-muted-foreground">{when}</span>
                <span className="truncate text-muted-foreground">· {version.title}</span>
                {/* The body checksum, so two versions can be told apart without opening both. */}
                <span className="font-mono text-xs text-muted-foreground">
                    {version.body_sha256?.slice(0, 12)}
                </span>
            </div>
            <Button variant="ghost" size="sm" onClick={onOpen}>
                {version.status === 'draft' ? (
                    <PenLine className="mr-2 h-4 w-4" />
                ) : (
                    <Eye className="mr-2 h-4 w-4" />
                )}
                {version.status === 'draft' ? 'Edit' : 'View'}
            </Button>
        </div>
    );
}
